import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, useFetch, useMutation } from '../api';

type GetNetworkConfig =
  ApiClient['v2']['workspaces'][':workspaceId']['network-configs'][':networkConfigId']['$get'];
export type NetworkConfigDetailedResponse = InferResponseType<GetNetworkConfig, 200>;

type CreateNetworkConfig =
  ApiClient['v2']['workspaces'][':workspaceId']['network-configs']['$post'];
export type CreateNetworkConfigRequest = InferRequestType<CreateNetworkConfig>['json'];
type CreateNetworkConfigResponse = InferResponseType<CreateNetworkConfig, 201>;

export function useWorkspaceNetworkConfig(workspaceId: number, networkConfigId: number) {
  return useFetch<NetworkConfigDetailedResponse>(
    `workspaces/${workspaceId}/network-configs/${networkConfigId}`,
    (client) =>
      client.v2.workspaces[':workspaceId']['network-configs'][':networkConfigId'].$get({
        param: {
          workspaceId: workspaceId.toString(),
          networkConfigId: networkConfigId.toString(),
        },
      })
  );
}

export function useAddWorkspaceNetworkConfig(
  workspaceId: number,
  actionOnErr: () => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/network-configs`;

  const addMutation = useMutation<CreateNetworkConfigResponse, CreateNetworkConfigRequest>(
    listCacheKey,
    (client, data) =>
      client.v2.workspaces[':workspaceId']['network-configs'].$post({
        param: { workspaceId: workspaceId.toString() },
        json: data,
      }),
    actionOnErr
  );

  return useCallback(
    async (data: CreateNetworkConfigRequest) => {
      const result = await addMutation(data);
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      return result;
    },
    [addMutation, mutate, listCacheKey]
  );
}

export function useDeleteWorkspaceNetworkConfig(
  workspaceId: number,
  actionOnErr: () => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/network-configs`;

  const deleteMutation = useMutation<number, number>(
    `${listCacheKey}/delete`,
    async (client, networkConfigId) => {
      const response = await client.v2.workspaces[':workspaceId']['network-configs'][
        ':networkConfigId'
      ].$delete({
        param: {
          workspaceId: workspaceId.toString(),
          networkConfigId: networkConfigId.toString(),
        },
      });
      // On failure, return the original response so parseResponse throws with
      // the RFC 7807 detail. On success the endpoint returns 204 (no body), so
      // synthesise a body carrying the deleted id — this lets the caller tell
      // success apart from the caught-error `undefined`.
      if (!response.ok) return response;
      return new Response(JSON.stringify(networkConfigId), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    },
    actionOnErr
  );

  return useCallback(
    async (networkConfigId: number) => {
      await deleteMutation(networkConfigId);
      await mutate((key: unknown) => typeof key === 'string' && key.startsWith(listCacheKey));
      await mutate(`workspaces/${workspaceId}/network-configs/${networkConfigId}`);
      return true;
    },
    [deleteMutation, mutate, listCacheKey, workspaceId]
  );
}
